'use client'

/**
 * ScorePanel — componente orchestratore.
 * Puro display: nessuna logica, solo props.
 *
 * Varianti:
 *  'card'      — compatto, per viewer mobile e desktop
 *  'broadcast' — TV graphics per OBS (sfondo scuro, numeri grandi)
 */
import { useEffect, useRef, useState } from 'react'
import { MatchState, TEAMS, TeamId } from '@/types/match'
import type { CommentaryPayload, MatchEvent } from '@/types/match'
import { isMatchPoint, isSetPoint } from '@/lib/volleyball/score-logic'
import MatchStatus from './MatchStatus'
import TeamBlock from './TeamBlock'
import ScoreBlock from './ScoreBlock'
import SetHistory from './SetHistory'

function formatOverlayClock(iso: string | null): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  return new Intl.DateTimeFormat('it-IT', {
    hour: '2-digit',
    minute: '2-digit',
  }).format(date)
}

interface Props {
  state: MatchState
  teamId?: TeamId
  accentColor?: string
  variant?: 'card' | 'broadcast'
  className?: string
}

export default function ScorePanel({
  state,
  teamId,
  accentColor = '#FE7200',
  variant = 'card',
  className = '',
}: Props) {
  const homeMP = isMatchPoint(state, 'home')
  const awayMP = isMatchPoint(state, 'away')
  const homeSP = !homeMP && isSetPoint(state, 'home')
  const awaySP = !awayMP && isSetPoint(state, 'away')

  if (variant === 'broadcast') {
    return (
      <div className={`score-panel-broadcast ${className}`}>
        <BroadcastPanel
          state={state}
          accentColor={accentColor}
          homeMP={homeMP} awayMP={awayMP}
          homeSP={homeSP} awaySP={awaySP}
        />
      </div>
    )
  }

  return (
    <div
      className={`score-panel-card rounded-xl overflow-hidden ${className}`}
      style={{ '--accent': accentColor } as React.CSSProperties}
    >
      <CardPanel
        state={state}
        accentColor={accentColor}
        teamId={teamId}
        homeMP={homeMP} awayMP={awayMP}
        homeSP={homeSP} awaySP={awaySP}
      />
    </div>
  )
}

// ─── Card variant ─────────────────────────────────────────────────────────────

/**
 * Il velo del servizio: un'area sola che scivola da una metà all'altra.
 * Animazione con Web Animations API (Safari 13.1+): una transizione CSS su
 * `transform` può essere persa quando il cambio arriva nello stesso frame di un
 * aggiornamento del DOM.
 */
function ServiceVeil({
  serveHome,
  battutaHome,
  battutaAway,
  tone,
  hidden = false,
}: {
  serveHome: boolean
  battutaHome: string
  battutaAway: string
  tone: 'dark' | 'light'
  hidden?: boolean
}) {
  const ref = useRef<HTMLDivElement>(null)
  const shownHome = useRef(serveHome)
  const stop = (color: string) => `color-mix(in srgb, ${color} ${tone === 'light' ? 18 : 36}%, transparent) 0%, color-mix(in srgb, ${color} ${tone === 'light' ? 6 : 10}%, transparent) 48%, transparent 74%`

  useEffect(() => {
    const el = ref.current
    const from = shownHome.current ? 0 : -50
    const to = serveHome ? 0 : -50
    shownHome.current = serveHome
    if (!el || hidden || from === to) return
    if (typeof el.animate !== 'function') return
    el.getAnimations().forEach((animation) => animation.cancel())
    const reduce = typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches
    if (reduce) {
      el.animate([{ opacity: 0.3 }, { opacity: 1 }], { duration: 420, easing: 'ease-out', fill: 'both' })
      return
    }
    el.animate(
      [{ transform: `translateX(${from}%)` }, { transform: `translateX(${to}%)` }],
      { duration: 1200, easing: 'ease-in-out', fill: 'both' },
    )
  }, [serveHome, hidden])

  return (
    <div aria-hidden data-service-veil className="pointer-events-none absolute inset-0 overflow-hidden">
      <div
        ref={ref}
        className="service-veil absolute inset-y-0 left-0 w-[200%]"
        data-serve={hidden ? 'off' : serveHome ? 'home' : 'away'}
        style={{
          backgroundImage: `linear-gradient(90deg, ${stop(battutaHome)}), linear-gradient(270deg, ${stop(battutaAway)})`,
          backgroundSize: '50% 100%',
          backgroundPosition: '0% 0%, 100% 0%',
          backgroundRepeat: 'no-repeat',
          transform: hidden ? 'translateX(0%)' : serveHome ? 'translateX(0%)' : 'translateX(-50%)',
          opacity: hidden ? 0 : 1,
        }}
      />
    </div>
  )
}

function TeamColumn({
  side, name, logo, score, setsWon, timeoutsUsed, hasService,
  isMatchPoint, isSetPoint, accentColor, displayMode = 'score',
}: {
  side: 'home' | 'away'
  name: string; logo?: string; score: number
  setsWon: number; timeoutsUsed: number; hasService: boolean
  isMatchPoint: boolean; isSetPoint: boolean
  accentColor: string
  displayMode?: 'score' | 'final-sets'
}) {
  const isRight = side === 'away'
  const isFinalSets = displayMode === 'final-sets'
  const scoreColor = isFinalSets ? 'white' : hasService ? accentColor : 'white'

  return (
    <div className={`relative overflow-hidden flex flex-col gap-2 p-3 ${isRight ? 'items-end' : 'items-start'}`}>
      {(isMatchPoint || isSetPoint) && !isFinalSets && (
        <span className={`absolute top-1/2 -translate-y-1/2 ${isRight ? 'left-3' : 'right-3'} text-[10px] font-bold uppercase px-2 py-1 rounded leading-none z-[2] flex flex-col items-center justify-center gap-0.5 ${
          isMatchPoint ? 'bg-[#FE7200] text-white' : 'bg-yellow-400/20 text-yellow-300'
        }`}>
          <span>{isMatchPoint ? 'MATCH' : 'SET'}</span>
          <span>POINT</span>
        </span>
      )}

      {/* ── Faded logo background ────────────────────────────────────────────── */}
      {logo && (
        <img
          src={logo}
          alt=""
          aria-hidden
          className="absolute inset-0 w-full h-full object-contain pointer-events-none select-none"
          style={{ opacity: 0.35, filter: 'blur(1px)', transform: 'scale(1.3)' }}
        />
      )}

      {/* ── Row 1: team name ─────────────────────────────────────────────────── */}
      <div className={`relative flex items-center gap-1 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
        <span className="font-display font-bold uppercase text-white text-sm leading-none truncate max-w-[120px]">
          {name}
        </span>
      </div>

      {/* ── Row 2: score + sets won ───────────────────────────────────────────── */}
      <div className={`relative flex items-end gap-2 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
        {/* Score */}
        <div className="relative">
          {isFinalSets && (
            <span className={`absolute -top-4 ${isRight ? 'right-0' : 'left-0'} text-[9px] font-bold uppercase tracking-[0.2em] text-white/45`}>
              Set
            </span>
          )}
          <span
            className="font-display font-bold tabular-nums leading-none transition-all duration-150"
            style={{ fontSize: '3.2rem', color: scoreColor }}
          >
            {score}
          </span>
        </div>

        {/* Sets won badge */}
        {!isFinalSets && (
          <div className="pb-1 flex flex-col items-center gap-1">
            <span
              className="inline-flex items-center justify-center rounded-full border text-white font-display font-bold leading-none"
              style={{
                width: '26px',
                height: '26px',
                borderColor: 'rgba(255,255,255,0.3)',
                background: 'rgba(255,255,255,0.08)',
                fontSize: '0.72rem',
              }}
            >
              {setsWon}
            </span>

            <div className={`flex gap-1 ${isRight ? 'flex-row-reverse' : 'flex-row'}`}>
              {Array.from({ length: 2 }, (_, i) => (
                <span
                  key={i}
                  className="w-1.5 h-1.5 rounded-full border border-white/20"
                  style={{ backgroundColor: i < timeoutsUsed ? '#FE7200' : 'transparent' }}
                  title={i < timeoutsUsed ? 'Timeout usato' : 'Timeout disponibile'}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function CardPanel({
  state, accentColor, teamId,
  homeMP, awayMP, homeSP, awaySP,
}: {
  state: MatchState; accentColor: string; teamId?: TeamId
  homeMP: boolean; awayMP: boolean; homeSP: boolean; awaySP: boolean
}) {
  const team = teamId ? TEAMS[teamId] : null
  const leagueLabel = team?.short ?? state.leagueShort ?? null
  const showFinalSets = state.isMatchFinished || state.status === 'finished'
  const [overlayCommentary, setOverlayCommentary] = useState('')
  const [overlayRenderedCommentary, setOverlayRenderedCommentary] = useState('')
  const [overlayVisible, setOverlayVisible] = useState(false)
  const [overlayClosing, setOverlayClosing] = useState(false)
  const [overlayTimestamp, setOverlayTimestamp] = useState<string | null>(null)
  const [overlayMode, setOverlayMode] = useState<'normal' | 'timeout'>('normal')
  const overlayHideTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const overlayClosingTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const prevStateRef = useRef<MatchState | null>(null)
  const wordByWordTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const overlayTextRef = useRef<HTMLParagraphElement>(null)
  const overlayScrollRef = useRef<HTMLDivElement>(null)
  const overlayModeRef = useRef<'normal' | 'timeout'>('normal')
  const overlayVisibleRef = useRef(false)

  const homeIsDinamo = state.homeTeam.toLowerCase().includes('dinamo')
  const awayIsDinamo = state.awayTeam.toLowerCase().includes('dinamo')
  const homeLogo = state.homeTeamLogo ?? (homeIsDinamo ? '/Dinamo.png' : undefined)
  const awayLogo = state.awayTeamLogo ?? (awayIsDinamo ? '/Dinamo.png' : undefined)
  const effectiveOverlayVisible = overlayVisible
  const effectiveOverlayText = overlayMode === 'timeout' ? overlayRenderedCommentary : overlayCommentary
  const effectiveOverlayTimestamp = overlayTimestamp

  const showOverlayText = (text: string, timestamp?: string, mode: 'normal' | 'timeout' = 'normal') => {
    const next = text.trim()

    if (overlayModeRef.current === 'timeout' && overlayVisibleRef.current && mode === 'normal') {
      if (!next) return
      setOverlayCommentary(next)
      setOverlayMode('timeout')
      overlayModeRef.current = 'timeout'
      setOverlayTimestamp(timestamp ?? new Date().toISOString())
      setOverlayVisible(true)
      overlayVisibleRef.current = true
      setOverlayClosing(false)
      if (overlayHideTimerRef.current) clearTimeout(overlayHideTimerRef.current)
      if (overlayClosingTimerRef.current) clearTimeout(overlayClosingTimerRef.current)
      overlayClosingTimerRef.current = setTimeout(() => setOverlayClosing(true), 28000)
      overlayHideTimerRef.current = setTimeout(() => {
        setOverlayClosing(false)
        setOverlayVisible(false)
        overlayVisibleRef.current = false
      }, 30000)
      if (wordByWordTimerRef.current) clearInterval(wordByWordTimerRef.current)

      const words = next.split(/\s+/)
      let index = 0
      setOverlayRenderedCommentary('')
      wordByWordTimerRef.current = setInterval(() => {
        index += 1
        setOverlayRenderedCommentary(words.slice(0, index).join(' '))
        if (index >= words.length && wordByWordTimerRef.current) {
          clearInterval(wordByWordTimerRef.current)
          wordByWordTimerRef.current = null
        }
      }, 220)
      return
    }

    if (!next) return

    setOverlayCommentary(next)
    setOverlayMode(mode)
    overlayModeRef.current = mode
    setOverlayTimestamp(timestamp ?? new Date().toISOString())
    setOverlayVisible(true)
    overlayVisibleRef.current = true
    setOverlayClosing(false)
    if (overlayHideTimerRef.current) clearTimeout(overlayHideTimerRef.current)
    if (overlayClosingTimerRef.current) clearTimeout(overlayClosingTimerRef.current)
    if (wordByWordTimerRef.current) clearInterval(wordByWordTimerRef.current)

    if (mode === 'timeout') {
      const words = next.split(/\s+/)
      let index = 0
      setOverlayRenderedCommentary('')
      wordByWordTimerRef.current = setInterval(() => {
        index += 1
        setOverlayRenderedCommentary(words.slice(0, index).join(' '))
        if (index >= words.length && wordByWordTimerRef.current) {
          clearInterval(wordByWordTimerRef.current)
          wordByWordTimerRef.current = null
        }
      }, 220)
      overlayClosingTimerRef.current = setTimeout(() => setOverlayClosing(true), 28000)
      overlayHideTimerRef.current = setTimeout(() => {
        setOverlayClosing(false)
        setOverlayVisible(false)
      }, 30000)
      return
    }

    setOverlayRenderedCommentary(next)
    overlayClosingTimerRef.current = setTimeout(() => setOverlayClosing(true), 8000)
    overlayHideTimerRef.current = setTimeout(() => {
      setOverlayClosing(false)
      setOverlayVisible(false)
    }, 10000)
  }

  const openTimeoutOverlay = () => {
    setOverlayMode('timeout')
    overlayModeRef.current = 'timeout'
    setOverlayCommentary('')
    setOverlayRenderedCommentary('')
    setOverlayTimestamp(new Date().toISOString())
    setOverlayVisible(true)
    overlayVisibleRef.current = true
    setOverlayClosing(false)
    if (overlayHideTimerRef.current) clearTimeout(overlayHideTimerRef.current)
    if (overlayClosingTimerRef.current) clearTimeout(overlayClosingTimerRef.current)
    if (wordByWordTimerRef.current) clearInterval(wordByWordTimerRef.current)
    overlayClosingTimerRef.current = setTimeout(() => setOverlayClosing(true), 28000)
    overlayHideTimerRef.current = setTimeout(() => {
      setOverlayClosing(false)
      setOverlayVisible(false)
      overlayVisibleRef.current = false
    }, 30000)
  }

  useEffect(() => {
    if (!teamId || state.scorebugCommentaryVisible === false) {
      setOverlayVisible(false)
      setOverlayClosing(false)
      if (overlayHideTimerRef.current) clearTimeout(overlayHideTimerRef.current)
      if (overlayClosingTimerRef.current) clearTimeout(overlayClosingTimerRef.current)
      return
    }

    const wsUrl = process.env.NEXT_PUBLIC_WS_URL
    if (!wsUrl) return

    const ws = new WebSocket(wsUrl)

    ws.onopen = () => {
      ws.send(JSON.stringify({ action: 'subscribe', matchId: teamId }))
    }

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data as string) as {
          type: string
          matchId?: string
          payload?: MatchEvent | CommentaryPayload
          timestamp?: string
        }

        if (msg.type === 'ping') {
          ws.send(JSON.stringify({ action: 'ping' }))
          return
        }

        if (msg.matchId !== teamId || !msg.payload) return

        if (msg.type === 'commentary') {
          showOverlayText((msg.payload as CommentaryPayload).commentary ?? '', msg.timestamp)
          return
        }

        if (msg.type === 'event') {
          const next = (msg.payload as MatchEvent).aiCommentary?.trim()
          if (next) showOverlayText(next, (msg.payload as MatchEvent).occurredAt)
        }
      } catch {}
    }

    return () => {
      if (overlayHideTimerRef.current) clearTimeout(overlayHideTimerRef.current)
      if (overlayClosingTimerRef.current) clearTimeout(overlayClosingTimerRef.current)
      if (wordByWordTimerRef.current) clearInterval(wordByWordTimerRef.current)
      ws.close()
    }
  }, [state.scorebugCommentaryVisible, teamId])

  useEffect(() => {
    if (overlayMode !== 'timeout') return
    const el = overlayScrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [overlayMode, overlayRenderedCommentary])

  useEffect(() => {
    const prev = prevStateRef.current
    prevStateRef.current = state
    if (!prev || state.scorebugCommentaryVisible === false) return

    const timeoutStarted =
      (state.status === 'timeout_home' || state.status === 'timeout_away') &&
      prev.status !== state.status

    if (!timeoutStarted) return

    openTimeoutOverlay()
  }, [state])

  return (
    <div className="relative bg-[#0d0d1a] border border-white/10 rounded-xl overflow-visible">
      <style>{`
        @keyframes commentaryBorderPulse {
          0%, 100% {
            box-shadow: 0 0 0 1px ${accentColor}66, 0 0 10px ${accentColor}33;
            border-color: ${accentColor}aa;
          }
          50% {
            box-shadow: 0 0 0 1px ${accentColor}cc, 0 0 18px ${accentColor}66;
            border-color: ${accentColor};
          }
        }
      `}</style>

      {/* Accent bar */}
      <div className="h-0.5 w-full" style={{ backgroundColor: accentColor }} />

      {/* Status bar */}
      <div className="flex items-center justify-between px-3 py-1.5 border-b border-white/6">
        <MatchStatus
          status={state.status}
          isTiebreak={state.isTiebreak}
          currentSet={state.currentSet}
        />
        {leagueLabel && (
          <span
            className="font-display font-bold uppercase tracking-widest text-[9px] text-white/80 px-2 py-0.5 rounded-full border border-white/15"
            style={{ background: accentColor + '30' }}
          >
            {leagueLabel}
          </span>
        )}
      </div>

      {/* Two-column grid */}
      <div className="relative">
        <div className="grid grid-cols-2 divide-x divide-white/6">
          {/* Velo del servizio: un'area sola con i due gradienti affiancati che
              scivola da una metà all'altra (Web Animations API, 1200 ms). */}
          <ServiceVeil
            serveHome={state.serviceTeam === 'home'}
            battutaHome={state.scorebugTheme?.serviceColor ?? accentColor}
            battutaAway={state.scorebugTheme?.serviceColor ?? accentColor}
            tone="dark"
            hidden={showFinalSets}
          />
          <TeamColumn
            side="home"
            name={state.homeTeam}
            logo={homeLogo}
            score={showFinalSets ? state.setsWon.home : state.currentScore.home}
            setsWon={state.setsWon.home}
            timeoutsUsed={state.timeoutsUsed.home}
            hasService={!showFinalSets && state.serviceTeam === 'home'}
            isMatchPoint={showFinalSets ? false : homeMP}
            isSetPoint={showFinalSets ? false : homeSP}
            accentColor={accentColor}
            displayMode={showFinalSets ? 'final-sets' : 'score'}
          />
          <TeamColumn
            side="away"
            name={state.awayTeam || '—'}
            logo={awayLogo}
            score={showFinalSets ? state.setsWon.away : state.currentScore.away}
            setsWon={state.setsWon.away}
            timeoutsUsed={state.timeoutsUsed.away}
            hasService={!showFinalSets && state.serviceTeam === 'away'}
            isMatchPoint={showFinalSets ? false : awayMP}
            isSetPoint={showFinalSets ? false : awaySP}
            accentColor={accentColor}
            displayMode={showFinalSets ? 'final-sets' : 'score'}
          />
        </div>

        <div
          className={`absolute inset-0 pointer-events-none transition-all duration-500 ${effectiveOverlayVisible ? 'opacity-100' : 'opacity-0'}`}
          style={{
            background: effectiveOverlayVisible ? 'linear-gradient(180deg, rgba(9,12,22,0.54), rgba(9,12,22,0.66))' : 'transparent',
            backdropFilter: effectiveOverlayVisible ? 'blur(3px)' : 'none',
            WebkitBackdropFilter: effectiveOverlayVisible ? 'blur(3px)' : 'none',
          }}
        >
          <div className="absolute -top-5 left-0 right-0 z-20 flex justify-center px-3">
            <div className="inline-flex items-center gap-2">
              <span className="font-display font-bold uppercase tracking-[0.16em] text-[8px]" style={{ color: accentColor, textShadow: '0 0 6px rgba(0,0,0,0.35)' }}>
                Commentary
              </span>
              {effectiveOverlayTimestamp && (
                <span className="font-display font-bold uppercase tracking-[0.12em] text-[7px]" style={{ color: accentColor, textShadow: '0 0 6px rgba(0,0,0,0.35)' }}>
                  {formatOverlayClock(effectiveOverlayTimestamp)}
                </span>
              )}
            </div>
          </div>

          <div className="absolute inset-[7px]">
            <div
              className="w-full h-full rounded-xl px-3 py-3 text-center flex items-center justify-center overflow-hidden"
              style={{
                background: 'linear-gradient(180deg, rgba(9,12,22,0.82), rgba(9,12,22,0.92))',
                borderTop: `1px solid ${accentColor}${overlayClosing ? 'cc' : '55'}`,
                borderBottom: `1px solid ${accentColor}${overlayClosing ? 'cc' : '55'}`,
                borderLeft: `1px solid ${accentColor}${overlayClosing ? 'cc' : '55'}`,
                borderRight: `1px solid ${accentColor}${overlayClosing ? 'cc' : '55'}`,
                boxShadow: overlayClosing
                  ? `0 0 0 1px ${accentColor}66, 0 0 14px ${accentColor}55`
                  : `0 0 0 1px ${accentColor}33`,
                animation: overlayClosing ? 'commentaryBorderPulse 0.72s ease-in-out infinite' : 'none',
              }}
            >
              <div
                ref={overlayScrollRef}
                className="max-w-[92%] max-h-full"
                style={{ overflow: 'hidden' }}
              >
                <p ref={overlayTextRef} className="font-display font-bold text-[12px] leading-tight text-white">
                  {effectiveOverlayText}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Parziali set (full-width) ─────────────────────────────────────────── */}
      <div className={`flex items-center px-3 py-2 border-t border-white/6 ${showFinalSets ? 'justify-center' : 'gap-2'}`}>
        {!showFinalSets && (
          <span className="font-display font-bold uppercase tracking-widest text-white/25 text-[9px] shrink-0">
            Parziali
          </span>
        )}
        <div className="flex items-center gap-1.5">
          {state.setHistory.length > 0 ? state.setHistory.map((set, i) => {
            const homeWon = set.home > set.away
            return (
              <span
                key={i}
                className="font-display font-bold tabular-nums text-[10px] leading-none px-2 py-0.5 rounded-full border"
                style={{
                  borderColor: homeWon ? 'rgba(255,255,255,0.5)' : 'rgba(255,255,255,0.18)',
                  background:  homeWon ? 'rgba(255,255,255,0.18)' : 'rgba(255,255,255,0.05)',
                }}
              >
                <span style={{ color: homeWon ? 'white' : 'rgba(255,255,255,0.35)' }}>{set.home}</span>
                <span className="mx-0.5" style={{ color: 'rgba(255,255,255,0.2)' }}>–</span>
                <span style={{ color: !homeWon ? 'white' : 'rgba(255,255,255,0.35)' }}>{set.away}</span>
              </span>
            )
          }) : (
            <span className="text-white/15 text-[10px] font-display font-bold tracking-widest">—</span>
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Broadcast variant (OBS) ──────────────────────────────────────────────────

function BroadcastPanel({
  state, accentColor,
  homeMP, awayMP, homeSP, awaySP,
}: {
  state: MatchState; accentColor: string
  homeMP: boolean; awayMP: boolean; homeSP: boolean; awaySP: boolean
}) {
  const showFinalSets = state.isMatchFinished || state.status === 'finished'

  return (
    <div
      className="w-full h-full flex flex-col"
      style={{ fontFamily: 'var(--font-display, sans-serif)' }}
    >
      {/* Top bar: competizione */}
      <div
        className="flex items-center justify-between px-6 py-2"
        style={{ backgroundColor: accentColor }}
      >
        <span className="text-white font-bold uppercase tracking-widest text-sm">
          Dinamo Molfetta
        </span>
        <MatchStatus
          status={state.status}
          isTiebreak={state.isTiebreak}
          currentSet={state.currentSet}
          className="text-white/80"
        />
        <SetHistory history={state.setHistory} maxSets={5} />
      </div>

      {/* Main area */}
      <div className="flex-1 flex items-center justify-between px-8 py-4 bg-[#0d0d1a]">
        {/* Casa */}
        <div className="flex-1 flex flex-col items-start gap-3">
          <TeamBlock
            side="home"
            name={state.homeTeam}
            logo={state.homeTeamLogo}
            setsWon={state.setsWon.home}
            timeoutsUsed={state.timeoutsUsed.home}
            hasService={!showFinalSets && state.serviceTeam === 'home'}
            setsDisplay={showFinalSets ? 'number' : 'dots'}
          />
        </div>

        {/* Score centrale */}
        <ScoreBlock
          homeScore={showFinalSets ? state.setsWon.home : state.currentScore.home}
          awayScore={showFinalSets ? state.setsWon.away : state.currentScore.away}
          serviceTeam={showFinalSets ? null : state.serviceTeam}
          isMatchPoint={showFinalSets ? undefined : { home: homeMP, away: awayMP }}
          isSetPoint={showFinalSets ? undefined : { home: homeSP, away: awaySP }}
          displayMode={showFinalSets ? 'sets' : 'score'}
          className="text-8xl px-8"
        />

        {/* Ospite */}
        <div className="flex-1 flex flex-col items-end gap-3">
          <TeamBlock
            side="away"
            name={state.awayTeam || '—'}
            logo={state.awayTeamLogo}
            setsWon={state.setsWon.away}
            timeoutsUsed={state.timeoutsUsed.away}
            hasService={!showFinalSets && state.serviceTeam === 'away'}
            setsDisplay={showFinalSets ? 'number' : 'dots'}
          />
        </div>
      </div>
    </div>
  )
}
